import { useMemo } from 'react';
import { useVisibilityRanking } from '@/hooks/api/useCompetitors';
import { useOwnedMovers } from '@/hooks/api/useSources';
import { useTrafficSummary } from '@/hooks/api/useTrafficSummary';
import { weekSummary } from '@/components/home/weekSummary';
import type { PlaybookListItem } from '@/types/playbooks';

/* ────────────────────────────────────────────────────────────────────────────
   WeekSummaryLine — one compact sentence in Overview's latest-benchmark band
   naming what moved this week.

   The enclosing page section owns the title, date, and drill-through; this
   component supplies only the measured change so it cannot grow into a second
   promotional page introduction.

   Every query it reads is already mounted elsewhere on this page — the
   visibility ranking by the overview comparison section, the owned movers by the summary
   itself, the playbooks by `OverviewPage` — so react-query serves all of it from
   cache and this line costs one extra request at most.

   Renders NOTHING when no clause qualifies (see `weekSummary`).
   ──────────────────────────────────────────────────────────────────────────── */

export function WeekSummaryLine({
  playbooks,
  fallback,
}: {
  playbooks: PlaybookListItem[];
  fallback?: string;
}) {
  const rankingQ = useVisibilityRanking();
  const moversQ = useOwnedMovers();
  // Same 7-day summary the AiVisitsModule beneath the chart reads — one cache entry.
  const trafficQ = useTrafficSummary(7);

  const clauses = useMemo(
    () =>
      weekSummary({
        ranking: rankingQ.data?.ranking ?? [],
        movers: moversQ.data?.movers ?? [],
        playbooks,
        periodStart: moversQ.data?.current_period?.date ?? null,
        visits: trafficQ.data?.visits ?? null,
      }),
    [rankingQ.data, moversQ.data, playbooks, trafficQ.data],
  );

  const sentence = clauses.length > 0 ? clauses.join(' · ') : fallback;
  if (!sentence) return null;

  return (
    <p className="text-sm leading-5 text-ink-content">
      {clauses.length > 0 && <span className="font-medium text-foreground">Since last week: </span>}
      {/* A `·`-joined list, not prose with "and" — these are three independent
          facts from three sources and the separator must not imply a sequence
          or a cause between them. */}
      {sentence}
    </p>
  );
}

export default WeekSummaryLine;
